import {
  Pagination,
  Table as NextUITable,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import React, { JSX, Key, ReactNode, useEffect, useState } from 'react';

import { TableItem, TableProps } from '../../types';
import { DEFAULT_ROWS } from '../../variables';
import { ContentLoadError } from '../ContentLoadError';
import { Flex } from '../Flex';
import { TableActions, TableSkeleton } from './partials';
import { ImagePreviewWrapper } from './Table.styled';

export { TableActions };

type TableImageProps = {
  children: ReactNode;
};
export const TableImageWrapper: React.FC<TableImageProps> = ({ children }) => (
  <ImagePreviewWrapper>
    {children}
  </ImagePreviewWrapper>
);

export const Table = <T extends TableItem>({
  data,
  onPageChange,
  totalPages,
  currentPage,
  isLoading,
  rows,
  rowsPerPageSkeleton,
  columns,
  isError,
  errorMessage,
  refetch,
  actions,
  showModal,
  selectionMode,
  getSelectedKey,
  defaultSelectedKeys,
  onClickRegenerate,
}: TableProps<T & TableItem>): JSX.Element | null => {
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (getSelectedKey) {
      getSelectedKey(selected);
    }
  }, [getSelectedKey, selected]);

  if (isError) {
    return (
      <ContentLoadError
        onRefreshClicked={() => {
          if (refetch) {
            refetch();
          }
        }}
        message={errorMessage}
      />
    );
  }

  const setSelectedKey = (e: Iterable<Key>) :void => {
    const selectedKeysArray = Array.from(e).map(String);
    setSelected(selectedKeysArray);
  };

  return (
    <div>
      <NextUITable
        removeWrapper
        className="h-full"
        radius="none"
        shadow="none"
        selectionMode={selectionMode}
        onSelectionChange={setSelectedKey}
        defaultSelectedKeys={defaultSelectedKeys}
      >
        <TableHeader className="text-gray-700 uppercase text-sm font-semibold">
          {columns?.map((column) => (
            <TableColumn
              key={column.key}
              className={`px-4 bg-white py-2 table-header-align-${column.align || 'start'} text-left w-[${column.align || 'auto'}]`}
            >
              {column.name}
            </TableColumn>
          ))}
        </TableHeader>
        <TableBody
          items={data || []}
          emptyContent="No rows to display."
          isLoading={isLoading}
          loadingContent={
            <TableSkeleton rows={rowsPerPageSkeleton || DEFAULT_ROWS} columns={columns.length} />
        }
          className="divide-y divide-x divide-gray-400 bg-white radius-0"
        >
          {(item) => (
            <TableRow key={item.id} className="hover:bg-blue-50">
              {(columnKey) => (
                <TableCell className="px-4 py-2 text-gray-700">
                  {rows(item, columnKey, actions, showModal, onClickRegenerate)}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </NextUITable>
      {!!totalPages && totalPages > 1 && !isLoading && (
        <Flex justifyContent="flex-end" margin="10px 0">
          <Pagination
            className="z-0"
            page={currentPage}
            key="pagination"
            showControls
            color="primary"
            variant="light"
            initialPage={currentPage}
            total={totalPages}
            size="sm"
            onChange={(page) => onPageChange(page)}
          />
        </Flex>
      )}
    </div>
  );
};
